'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import seoReviewsDIModule from '@actr/stores/src/modules/seo/reviews';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import OtherPopularDirections from '@/components/avia/other-popular-directions';
import Recommendations from '@/components/avia/recommendations';
import FlightInformation from '@/components/avia/routes-in-city/flight-information';
import Tags from '@/components/avia/routes-in-city/tags';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import CityToCityAirlinesBlock from '@/components/page-constucts/city-to-city-airlines-block';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import ReviewsBlock from '@/components/page-constucts/reviews-block';
import SideNavBar from '@/components/page-constucts/side-navigation-bar';
import WhereToFly from '@/components/page-constucts/where-to-fly';
import { SeoRoutesInCity } from '@/services/models/avia/routes-from-city-in-city';
import { getContentBlocks } from '@/utils/get-content-blocks';
import getNavBarItems from '@/utils/nav-bar';
import { imageObjectJSONLD, organizationJSONLD, postalAdressJSONLD } from '@/utils/schema-org/organization';

import * as Styled from './style.css';

export type BaseRoutesInCityPageProps = {
    data: SeoRoutesInCity;
    pathFrom: string;
    pathIn: string;
}

const BaseRoutesInCityPage: FC <BaseRoutesInCityPageProps & PropsWithChildren> = observer(({ data, pathFrom, pathIn }) => {
  const { search_form_title, content, tags, breadcrumbs, destination_country, route_information } = data;
  const aviaSearchForm = useInject(AviaSearchFormStore);
  const { flight_information, recommendations,
    where_to_fly, faq, feedback, advantages, app_banner, info, other_popular_directions } = content || {};
  const itemsForNavBar = getNavBarItems(content);

  useEffect(() => {
    aviaSearchForm.stopStore();

    if (content && recommendations?.list) {
      const seoSegmentFrom = {
        iata: recommendations.list[0].dep.airport.code,
        name: recommendations.list[0].dep.city.title,
        airport_aggregation_iata: recommendations.list[0].dep.airport.code,
        country: {
          name: recommendations.list[0].dep.city.title,
          iata: recommendations.list[0].dep.airport.code,
        },
        city: {
          name: recommendations.list[0].dep.city.title,
          iata: recommendations.list[0].dep.airport.code,
        },
      };
      const seoSegmentTo = {
        iata: recommendations.list[0].arr.airport.code,
        name: recommendations.list[0].arr.city.title,
        airport_aggregation_iata: recommendations.list[0].arr.airport.code,
        country: {
          name: recommendations.list[0].arr.city.title,
          iata: recommendations.list[0].arr.airport.code,
        },
        city: {
          name: recommendations.list[0].arr.city.title,
          iata: recommendations.list[0].arr.airport.code,
        },
      };
      const routeSegments = [{
        ...aviaSearchForm.defaultSegment,
        from: seoSegmentFrom,
        to: seoSegmentTo,
      }];

      runInAction(() => {
        aviaSearchForm.setSegments(routeSegments);
        aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
      });
    }
  }, [aviaSearchForm, content, recommendations]);

  const crumbTo = [{
    title: 'Авиабилеты',
    path: '/',
  }, {
    path: '/routes',
    title: 'Маршрут',
  }, {
    path: `/routes/${pathFrom}`,
    title: route_information?.from_city_name || '',
  }, {
    path: `/routes/${pathIn}`,
    title: breadcrumbs,
  }];

  const contentBlocks = content ? [
    {
      key: 'airline-flights',
      condition: !!content['airline-flights']?.list?.recommendations,
      render: () => (
        <CityToCityAirlinesBlock airlines={content['airline-flights']} id="airline-flights" />
      )
    },
    {
      key: 'info',
      condition: !!info,
      render: () => info ? (
        <ContentBlock id="info" tagProps={{ itemScope: true, itemType: 'https://schema.org/Article' }}>
          <InnerHtmlTextBlock html={info.description} title={info.title} />
        </ContentBlock>
      ) : null
    },
    {
      key: 'flight_information',
      condition: !!flight_information?.list,
      render: () => (
        flight_information
          ? (<FlightInformation flight_information={flight_information} />) : null
      )
    },
    {
      key: 'other_popular_directions',
      condition: !!other_popular_directions && !!other_popular_directions.list,
      render: () => (
        other_popular_directions ? (
          <OtherPopularDirections data={other_popular_directions} />
        ) : null
      )
    },
    {
      key: 'recommendations',
      condition: !!recommendations && !!recommendations.list,
      render: () => (
        recommendations ? (
          <Recommendations id="recommendations" recommendations={recommendations} />
        ) : null
      )
    },
    {
      key: 'where_to_fly',
      condition: !!where_to_fly && !!where_to_fly.list,
      render: () => (
        where_to_fly ? (
          <WhereToFly
            title={where_to_fly.title}
            subtitle={where_to_fly.subtitle}
            cities={where_to_fly.list.cities}
            countries={where_to_fly.list.countries}
            directions={where_to_fly.list.directions}
            allCitiesLink={destination_country}
          />
        ) : null
      )
    },
    {
      key: 'faq',
      condition: !!faq?.list,
      render: () => (
        faq ? (
          <Faq list={faq?.list} title={faq.title} />
        ) : null
      )
    },
    {
      key: 'feedback',
      condition: !!feedback?.list,
      render: () => (
        feedback ? (
          <ReviewsBlock title={feedback.title} feedback={feedback?.list} />
        ) : null
      )
    },
    {
      key: 'advantages',
      condition: !!advantages?.list?.enable,
      render: () => (<ServiceAdvantages />)
    }
  ] : [];

  const blocks = getContentBlocks({ content, contentBlocks });

  return (
    <>
      <SearchFormHeader
        title={search_form_title || ''}
      />
      {!!tags?.length && <Tags tags={tags} />}
      <Container>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify([
            organizationJSONLD(),
            imageObjectJSONLD(),
            postalAdressJSONLD()]) }}
        />
        <Space size={Sizes.L} />
        <Breadcrumb crumb={crumbTo} dynamic={false} />
        <Space size={Sizes.L} />
        <Container className={Styled.ColumnWrapper}>
          <SideNavBar items={itemsForNavBar} isAppBannerEnable={!!app_banner?.list?.enable} isHideNavBarOnMobile={true} />
          <div className={Styled.MainInfo}>
            {blocks.map(block => (
              <React.Fragment key={block.key}>
                {block.render()}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </Container>
    </>
  );
});

export default loadDIModules(BaseRoutesInCityPage, aviaSearchDIModule, seoReviewsDIModule);
